import { mulberry32, seedFrom } from "./random.js";
/**
 * Stimulus generation for the games added after the M1 slice.
 *
 * Same rule as the original two: every stimulus is reproducible from
 * `(formId, trialIndex, difficulty)` alone, so a trial stays re-scorable years later from
 * the event record without the client build that produced it.
 */
// ---------------------------------------------------------------------------
// Backward span — recall the sequence in reverse.
// ---------------------------------------------------------------------------
/**
 * What the child has to tap, given what they were shown.
 *
 * The rule lives with the task rather than in the renderer, because it is the difference
 * between two constructs. Backward recall loads working memory more heavily than forward —
 * the sequence has to be held AND transformed — which is why the two are separate families
 * and why their spans must never be pooled into one estimate.
 */
export function expectedSpanResponse(task, stimulus) {
    return recallDirection(task) === "backward" ? [...stimulus].reverse() : [...stimulus];
}
export function recallDirection(task) {
    return task.params["recallDirection"] === "backward" ? "backward" : "forward";
}
const gridSize = (task) => ({
    cols: Number(task.params["gridCols"]),
    rows: Number(task.params["gridRows"]),
});
/** Mirrors a set of filled cells left-to-right. */
function mirror(cells, n) {
    return cells.map((i) => {
        const r = Math.floor(i / n);
        const c = i % n;
        return r * n + (n - 1 - c);
    });
}
const canon = (cells) => [...cells].sort((a, b) => a - b).join(",");
/**
 * A pattern is usable here only if its mirror image is a different shape.
 *
 * Mirror-symmetric patterns fail: their reflection is identical, so the trial would have no odd
 * one at all while looking perfectly reasonable on screen. This is the check that makes the
 * generator honest rather than plausible.
 */
function usableBase(cells, n) {
    return canon(cells) !== canon(mirror(cells, n));
}
/**
 * Odd-one-out stimulus: three identical shapes and one mirrored.
 *
 * **The first version of this was wrong and unplayable.** It made the three "same" options
 * ROTATIONS of one pattern, on the theory that the odd one is the reflection no rotation can
 * reach. That is a real construct — mental rotation — and it is also incoherent from the
 * player's side: four rotations of a dot pattern look like four different pictures, so a child
 * told "one is different" sees four different ones and no way in. Understanding the trial
 * required already knowing that rotation preserves identity, which is the thing being tested.
 *
 * The same-shape options are now literally identical, so "three are the same, one is not" is
 * true to the eye and needs no explanation. The difference is still a REFLECTION, which keeps it
 * a spatial discrimination — the same left/right distinction behind b/d confusion — rather than
 * spot-the-red-one. And every option still has the same number of filled cells, because
 * mirroring preserves the count, so counting is not a shortcut.
 *
 * `difficulty` is the number of filled cells: more cells make the reflection subtler.
 */
export function generateOddOneOutStimulus(task, formId, trialIndex, difficulty) {
    const { cols, rows } = gridSize(task);
    if (cols !== rows)
        throw new Error(`${task.id} needs a square grid to mirror on`);
    const n = cols;
    const cells = n * n;
    const optionCount = Math.max(3, Number(task.params["options"] ?? 4));
    if (difficulty < 2 || difficulty > cells - 1) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * Draw until a usable base turns up. Bounded, and the bound is not a fallback that relaxes the
     * requirement: exhausting it throws, because a trial with no odd one must never reach a child.
     */
    let base = null;
    for (let attempt = 0; attempt < 400 && !base; attempt++) {
        const candidate = new Set();
        while (candidate.size < difficulty)
            candidate.add(Math.floor(rand() * cells));
        const drawn = [...candidate];
        if (usableBase(drawn, n))
            base = drawn;
    }
    if (!base) {
        throw new Error(`no usable pattern for ${task.id} at difficulty ${difficulty} — every draw was ` +
            `mirror-symmetric, so no trial would have had an odd one out`);
    }
    // Identical copies, so a child can see at a glance which one is not one of them.
    const options = [];
    for (let i = 0; i < optionCount - 1; i++)
        options.push([...base]);
    const correctIndex = Math.floor(rand() * optionCount);
    options.splice(correctIndex, 0, mirror(base, n));
    return { options, correctIndex };
}
// ---------------------------------------------------------------------------
// Build the same shape — reproduce a target on an empty grid.
// ---------------------------------------------------------------------------
/**
 * The target to copy.
 *
 * The target stays VISIBLE while the child builds, which is what makes this a construction
 * task rather than a memory one. That is deliberate: it gives the product a third kind of
 * play, and it is why this task is scored on accuracy and never feeds the working-memory
 * span estimate.
 *
 * `difficulty` is how many cells have to be placed.
 */
export function generateBuildTarget(task, formId, trialIndex, difficulty) {
    const { cols, rows } = gridSize(task);
    const cells = cols * rows;
    if (difficulty < 1 || difficulty > cells - 1) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const chosen = new Set();
    while (chosen.size < difficulty)
        chosen.add(Math.floor(rand() * cells));
    return [...chosen].sort((a, b) => a - b);
}
/** Exact reproduction. Order of placement does not matter; the finished shape does. */
export function scoreBuildTrial(target, placed) {
    return canon(target) === canon(placed);
}
//# sourceMappingURL=games.js.map